/**
 * Landing v3 — copy that streams in word by word, shared by the desktop scene
 * and the phone layout: Ka's line and every block's headline.
 */
import { motion } from 'framer-motion';

import { cn } from '@/lib/utils';

import { EASE_OUT } from './landing-ka';

// ── Professor Ka interstitial copy (Figma 12:242 / 12:354 / 12:584) ─────────

/**
 * The three mid-page Ka lines, one per interstitial, split into i18n segments
 * so each language can place the highlighted word naturally. Whole keys
 * (`landingV3.ka.*`); `hl` renders in primary-600, exactly as the Figma frames.
 * The closing section is its own thing — see FinalProof (Figma 15:1412).
 */
export const KA_LINES: {
  size: 'd1' | 'd2';
  rows: { key: string; hl?: boolean }[][];
}[] = [
  // The one interstitial before the deck: "First, upload everything you
  // have, and start creating…" (the planning and study lines are gone —
  // the cards run as one stack).
  {
    size: 'd1',
    rows: [
      [
        { key: 'landingV3.ka.ka1a' },
        { key: 'landingV3.ka.ka1b', hl: true },
        { key: 'landingV3.ka.ka1c' },
      ],
      [{ key: 'landingV3.ka.ka1d' }],
    ],
  },
];

/** One word of a streamed line: duration + per-word delay. */
const WORD_DURATION = 0.42;
const WORD_STAGGER = 0.055;
/** Seconds a triggered line of `words` words takes to stream in, from its
 *  delay — for whatever follows it. */
export const lineStreamSeconds = (words: number) =>
  Math.max(0, words - 1) * WORD_STAGGER + WORD_DURATION;

/**
 * One word of a streamed line. Mounted from the start but held invisible
 * until `play` latches true, then it animates in ONCE on a time-based delay.
 * Because `play` never un-latches, scrolling back and forth can't re-run or
 * reverse the stream.
 */
export function KaWord({
  text,
  hl,
  leadingSpace,
  index,
  play,
  delay = 0,
  reduceMotion,
}: {
  text: string;
  hl?: boolean;
  leadingSpace: boolean;
  index: number;
  play: boolean;
  delay?: number;
  reduceMotion: boolean;
}) {
  return (
    <span>
      {leadingSpace && ' '}
      <motion.span
        initial={{ opacity: 0, y: 10 }}
        animate={play ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
        transition={
          reduceMotion
            ? { duration: 0 }
            : {
                duration: WORD_DURATION,
                delay: play ? delay + index * WORD_STAGGER : 0,
                ease: EASE_OUT,
              }
        }
        className={cn('inline-block', hl && 'text-primary-600')}>
        {text}
      </motion.span>
    </span>
  );
}

/** Split an i18n segment list into words, keeping trailing punctuation
 *  ("...") attached to the previous word instead of orphaning it. */
export function toWords(segments: { text: string; hl?: boolean }[]) {
  return segments.flatMap((seg) => {
    const attach = /^[.,!?…:;]/.test(seg.text);
    return seg.text
      .split(' ')
      .filter(Boolean)
      .map((word, wi) => ({ word, hl: seg.hl, attach: attach && wi === 0 }));
  });
}

/** A whole line of copy that reveals word-by-word once its section arrives. */
export function TriggeredLine({
  segments,
  arrived,
  delay,
  reduceMotion,
  className,
  as: Tag = 'h2',
}: {
  segments: { text: string; hl?: boolean }[];
  arrived: boolean;
  delay: number;
  reduceMotion: boolean;
  className?: string;
  /** An H2 by default: a triggered line is its section's headline (the map,
   *  the posts, the close), and the page outline must say so. Ka's line on
   *  the phone layout is speech, not a heading — a `p`. */
  as?: 'h2' | 'p';
}) {
  const tokens = toWords(segments);
  return (
    <Tag className={className}>
      {tokens.map((token, i) => (
        <KaWord
          key={i}
          text={token.word}
          hl={token.hl}
          leadingSpace={i > 0 && !token.attach}
          index={i}
          play={arrived}
          delay={delay}
          reduceMotion={reduceMotion}
        />
      ))}
    </Tag>
  );
}
